import { Link } from "react-router-dom";
import { ArrowRight, AlertTriangle, Zap, ShieldCheck } from "lucide-react";
import { HeroDiagram } from "@/components/HeroDiagram";
import { SectionHead, Metrics, Partners, ClientsCarousel, Testimonials, Technologies, ProductCard, ServiceIcon, CtaBand, Provisional, useList } from "@/components/Blocks";
import { useT } from "@/lib/i18n";
import { useAuth, isStaff } from "@/lib/auth";
import { useSeo } from "@/lib/analytics";

const PILLARS = [
  { icon: AlertTriangle, t: "Qué problemas resolvemos", d: "Procesos manuales, sistemas desconectados, doble digitación y operaciones sin trazabilidad." },
  { icon: Zap, t: "Cómo lo hacemos", d: "Software a medida, integraciones con aduanas y sistemas contables, y plataformas cloud escalables." },
  { icon: ShieldCheck, t: "Qué obtiene su organización", d: "Operaciones digitalizadas, automatizadas y seguras, con acompañamiento durante la evolución de sus sistemas." },
];

function Hero() {
  const { t } = useT();
  return (
    <section className="relative overflow-hidden bg-navy noise" data-testid="home-hero">
      <div className="absolute inset-0 grid-bg" />
      <div className="absolute -right-40 -top-40 h-[480px] w-[480px] rounded-full bg-[color:var(--cc-blue)] opacity-20 blur-[120px]" />
      <div className="wrap relative grid items-center gap-16 py-20 lg:grid-cols-[1.1fr_1fr] lg:py-28">
        <div>
          <p className="rise font-mono text-xs uppercase tracking-[0.25em] text-signal">Software empresarial · SaaS · Cloud</p>
          <h1 className="rise mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight text-white" style={{ animationDelay: ".08s" }} data-testid="hero-title">
            {t("heroTitle")}
          </h1>
          <p className="rise mt-6 max-w-xl text-base md:text-lg leading-relaxed text-slate-300" style={{ animationDelay: ".16s" }}>{t("heroSub")}</p>
          <div className="rise mt-10 flex flex-wrap gap-3" style={{ animationDelay: ".24s" }}>
            <Link to="/soluciones" className="btn-cc" data-testid="hero-explore-btn">{t("explore")} <ArrowRight className="h-4 w-4" /></Link>
            <Link to="/demo" className="btn-ghost-cc border-white/20 text-white hover:bg-white/10" data-testid="hero-demo-btn">{t("demo")}</Link>
          </div>
          <div className="rise mt-12 flex flex-wrap gap-x-8 gap-y-3 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-400" style={{ animationDelay: ".32s" }}>
            <span>Desarrollo a medida</span><span>Integraciones</span><span>Facturación electrónica</span><span>Soluciones cloud</span>
          </div>
        </div>
        <HeroDiagram />
      </div>
    </section>
  );
}

export default function Home() {
  const { t } = useT();
  const { user } = useAuth();
  useSeo({ title: "Tecnología que impulsa tu negocio", jsonLd: { "@context": "https://schema.org", "@type": "Organization", name: "Cloud Center Costa Rica", url: window.location.origin, email: "info@cloudcentercr.com", logo: window.location.origin + "/logo-full.png" } });
  const metrics = useList("/public/metrics") || [];
  const services = useList("/public/services") || [];
  const products = useList(`/public/products${isStaff(user) ? "?preview=1" : ""}`, [user]) || [];
  const clients = useList("/public/clients") || [];
  const testimonials = useList("/public/testimonials") || [];
  const tech = useList("/public/technologies") || [];
  const partners = useList("/public/partners") || [];
  return (
    <>
      <Hero />
      <section className="wrap -mt-px py-16 md:py-20"><Metrics items={metrics} />
        {partners.length > 0 && <div className="mt-16"><SectionHead eyebrow="Alianzas" title="Alianzas estratégicas" sub="Trabajamos junto a socios que complementan nuestra capacidad tecnológica." /><Partners items={partners} /></div>}
      </section>

      <section className="wrap pb-16 md:pb-24">
        <div className="grid gap-6 md:grid-cols-3">
          {PILLARS.map((p, i) => (
            <div key={p.t} className="card-cc" data-testid={`pillar-${i}`}>
              <p className="font-mono text-xs text-slate-400">0{i + 1}</p>
              <p.icon className="mt-6 h-6 w-6 text-brand" />
              <h3 className="mt-4 font-display text-xl font-semibold">{p.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{p.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[color:var(--cc-bg)] py-16 md:py-24 border-y border-slate-200">
        <div className="wrap">
          <SectionHead eyebrow="Servicios tecnológicos" title="Capacidad técnica para cada etapa de su transformación digital"
            right={<Link to="/servicios" className="text-sm font-semibold text-brand inline-flex items-center gap-1" data-testid="home-services-link">Todos los servicios <ArrowRight className="h-4 w-4" /></Link>} />
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {services.map((s) => (
              <Link key={s.id} to={`/servicios/${s.slug}`} className="card-cc card-cc-hover group !p-6" data-testid={`home-service-${s.slug}`}>
                <span className="grid h-11 w-11 place-items-center rounded-xl bg-blue-50 text-brand transition-colors group-hover:bg-brand group-hover:text-white"><ServiceIcon name={s.icon} /></span>
                <h3 className="mt-5 font-display text-base font-semibold">{s.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{s.summary}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="wrap py-16 md:py-24">
        <SectionHead eyebrow="Nuestros productos" title="Plataformas y sistemas listos para su operación" sub="Software propio para facturación electrónica, gestión empresarial y más, con modalidades de suscripción, licenciamiento y cotización."
          right={<Link to="/soluciones" className="text-sm font-semibold text-brand inline-flex items-center gap-1" data-testid="home-solutions-link">Ver catálogo <ArrowRight className="h-4 w-4" /></Link>} />
        {products.length ? (
          <div className="grid gap-6 md:grid-cols-3">{products.slice(0, 3).map((p) => <ProductCard key={p.id} p={p} />)}</div>
        ) : (
          <Provisional testid="products-placeholder" title="Catálogo comercial en preparación" text="Los productos se publicarán cuando su disponibilidad, precios y derechos de comercialización sean aprobados. Mientras tanto, puede solicitar una demostración." />
        )}
      </section>

      <section className="border-y border-slate-200 bg-white py-16 md:py-24">
        <div className="wrap">
          <SectionHead eyebrow="Clientes" title={t("trustTitle")} />
          <ClientsCarousel clients={clients} />
        </div>
      </section>

      <section className="wrap py-16 md:py-24">
        <SectionHead eyebrow="Testimonios" title={t("testiTitle")} />
        <Testimonials items={testimonials} />
      </section>

      <section className="wrap pb-8">
        <SectionHead eyebrow="Stack tecnológico" title={t("techTitle")} sub="Elegimos tecnologías probadas en entornos empresariales para construir soluciones seguras, escalables y mantenibles." />
        <Technologies items={tech} />
      </section>
      <CtaBand />
    </>
  );
}
